import { createElement, KeyboardEvent, RefObject, useEffect, useRef } from 'react';
import MonthYearCaption, { IMonthYearCaption } from './MonthYearCaption';
import { TDayPickerInput } from './types';

/**
 * Wires the month/year caption into a `<DayPickerInput />`. Pass `ref={dayPickerInputRef}`
 * and `keepFocus={false}`, and spread `monthYearProps` last into its `dayPickerProps`.
 *
 * `keepFocus` has to be off or the popup pulls focus back to the input as soon as a dropdown
 * opens. It then no longer closes on input blur, so outside click, Escape and Tab are here.
 */
const useMonthYearCaption = (containerRef: RefObject<HTMLElement>) => {
  const dayPickerInputRef = useRef<TDayPickerInput>(null);

  // `DayPickerInput` overwrites the `month` prop with its own state, so navigate imperatively.
  const handleMonthChange = (month: Date) =>
    dayPickerInputRef.current?.getDayPicker()?.showMonth(month);

  // The picker forwards its wrapper's key events here; its own Escape needs the input focused.
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      dayPickerInputRef.current?.hideDayPicker();
    }
  };

  useEffect(() => {
    const container = containerRef.current;

    // `mousedown`, not `click`: an `<option>` can be detached by the re-render its own
    // selection triggers, reading as a click outside. `hideDayPicker` no-ops when closed.
    const handleDocumentMouseDown = (e: MouseEvent) => {
      if (container?.contains(e.target as Node)) {
        return;
      }
      dayPickerInputRef.current?.hideDayPicker();
    };

    const handleFocusOut = (e: FocusEvent) => {
      const nextTarget = e.relatedTarget as Node | null;

      // Null when focus is only dropped, such as clicking a gap in the calendar.
      if (!nextTarget || container?.contains(nextTarget)) {
        return;
      }
      dayPickerInputRef.current?.hideDayPicker();
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    container?.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      container?.removeEventListener('focusout', handleFocusOut);
    };
  }, [containerRef]);

  // An element, not a component: the picker clones it, keeping the caption's type stable so
  // an open dropdown survives a re-render.
  const captionElement = createElement(MonthYearCaption, {
    onMonthChange: handleMonthChange,
  } as IMonthYearCaption);

  return { dayPickerInputRef, monthYearProps: { captionElement, onKeyDown: handleKeyDown } };
};

export default useMonthYearCaption;
